import { useRef, useEffect, useState } from 'react';
import styled from 'styled-components';
import { PhotoProvider, PhotoView } from 'react-photo-view';
import 'react-photo-view/dist/react-photo-view.css';

const Canvas = styled.canvas`
  width: 100%;
  height: 100%;
`;

const Carrier = ({ token, cardImage, giftCardText, senderName }) => {
  const canvasRef = useRef(null);

  const [viewerSrc, setViewerSrc] = useState(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const backgroundImg = new Image();
    const cardImg = new Image();

    const drawImage = () => {
      const container = canvas.parentElement;
      if (!container) return;

      const rect = container.getBoundingClientRect();
      const displayWidth = rect.width;
      const displayHeight = rect.height;

      canvas.style.width = `${displayWidth}px`;
      canvas.style.height = `${displayHeight}px`;

      const dpr = window.devicePixelRatio || 1;
      canvas.width = displayWidth * dpr;
      canvas.height = displayHeight * dpr;

      ctx.clearRect(0, 0, displayWidth, displayHeight);

      ctx.scale(dpr, dpr);

      const size = Math.min(displayWidth, displayHeight);
      const x = (displayWidth - size) / 2;
      const y = (displayHeight - size) / 2;

      if (backgroundImg.complete) {
        ctx.drawImage(backgroundImg, x, y, size, size);
      }

      if (cardImg && cardImg.complete && cardImage) {
        const RC80_ASPECT_RATIO = 85.6 / 53.98;

        const cardWidth = size * 0.23;
        const cardHeight = cardWidth / RC80_ASPECT_RATIO;
        const cardX = (displayWidth - size) / 2 + size / 3.5 - cardWidth / 2;
        const cardY = size / 3.5;

        ctx.save();
        const insetTop = cardHeight * 0.06;
        const insetRight = cardWidth * 0.04;
        const insetBottom = cardHeight * 0.06;
        const insetLeft = cardWidth * 0.04;
        const borderRadius = 2;

        const clipX = cardX + insetLeft;
        const clipY = cardY + insetTop;
        const clipWidth = cardWidth - insetLeft - insetRight;
        const clipHeight = cardHeight - insetTop - insetBottom;

        ctx.beginPath();
        ctx.moveTo(clipX + borderRadius, clipY);
        ctx.lineTo(clipX + clipWidth - borderRadius, clipY);
        ctx.arcTo(
          clipX + clipWidth,
          clipY,
          clipX + clipWidth,
          clipY + borderRadius,
          borderRadius,
        );
        ctx.lineTo(clipX + clipWidth, clipY + clipHeight - borderRadius);
        ctx.arcTo(
          clipX + clipWidth,
          clipY + clipHeight,
          clipX + clipWidth - borderRadius,
          clipY + clipHeight,
          borderRadius,
        );
        ctx.lineTo(clipX + borderRadius, clipY + clipHeight);
        ctx.arcTo(
          clipX,
          clipY + clipHeight,
          clipX,
          clipY + clipHeight - borderRadius,
          borderRadius,
        );
        ctx.lineTo(clipX, clipY + borderRadius);
        ctx.arcTo(clipX, clipY, clipX + borderRadius, clipY, borderRadius);
        ctx.closePath();
        ctx.clip();

        ctx.drawImage(cardImg, cardX, cardY, cardWidth, cardHeight);

        ctx.restore();
      }

      if (token?.symbol) {
        ctx.save();
        const fontSize = size * 0.03;

        ctx.font = `${fontSize}px Inter, sans-serif`;
        ctx.fillStyle = 'white';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';

        const textX = (displayWidth - size) / 2 + size / 3.5;
        const textY = size / 2.1;

        const line1 = "You've been gifted";
        ctx.fillText(line1, textX, textY);

        const line2 = token.symbol;
        const lineHeight = fontSize * 1.3;
        ctx.fillText(line2, textX, textY + lineHeight);

        ctx.restore();
      }

      if (giftCardText) {
        ctx.save();

        const fontSize = size * 0.014;
        ctx.font = `${fontSize}px Inter, sans-serif`;
        ctx.fillStyle = 'white';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';

        const textX = x + size / 3.5;
        const textY = size / 1.75;

        const text = giftCardText.slice(0, 190);

        const maxCharsPerLine = 35;

        const words = text;
        const lines = [];
        let currentLine = '';

        for (let index = 0; index < words.length; index++) {
          const word = words[index];
          const followingCurrentLine = currentLine + word;
          if (followingCurrentLine.length > maxCharsPerLine) {
            lines.push(followingCurrentLine);
            currentLine = '';
          } else {
            currentLine = followingCurrentLine;
            if (index === words.length - 1) {
              lines.push(followingCurrentLine);
            }
          }
        }

        const lineHeight = fontSize * 1.2;
        lines.forEach((line, index) => {
          ctx.fillText(line, textX, textY + index * lineHeight);
        });

        ctx.restore();
      }

      if (senderName) {
        ctx.save();

        const fontSize = size * 0.012;
        ctx.font = `${fontSize}px Inter, sans-serif`;
        ctx.fillStyle = 'white';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';

        const textX = x + size / 3.5;
        const textY = size / 1.4;

        const text = `From: ${senderName.slice(0, 32)}`;
        ctx.fillText(text, textX, textY);

        ctx.restore();
      }

      // Export at higher resolution
      const exportScale = 3; // 3x resolution for high-quality export
      const exportCanvas = document.createElement('canvas');
      exportCanvas.width = displayWidth * exportScale;
      exportCanvas.height = displayHeight * exportScale;
      const exportCtx = exportCanvas.getContext('2d');

      // Scale context for high resolution
      exportCtx.scale(exportScale, exportScale);

      // Redraw everything on the export canvas
      const exportSize = Math.min(displayWidth, displayHeight);
      const exportX = (displayWidth - exportSize) / 2;
      const exportY = (displayHeight - exportSize) / 2;

      if (backgroundImg.complete) {
        exportCtx.drawImage(
          backgroundImg,
          exportX,
          exportY,
          exportSize,
          exportSize,
        );
      }

      if (cardImg && cardImg.complete && cardImage) {
        const RC80_ASPECT_RATIO = 85.6 / 53.98;

        const cardWidth = exportSize * 0.23;
        const cardHeight = cardWidth / RC80_ASPECT_RATIO;
        const cardX =
          (displayWidth - exportSize) / 2 + exportSize / 3.5 - cardWidth / 2;
        const cardY = exportSize / 3.5;

        exportCtx.save();
        const insetTop = cardHeight * 0.06;
        const insetRight = cardWidth * 0.04;
        const insetBottom = cardHeight * 0.06;
        const insetLeft = cardWidth * 0.04;
        const borderRadius = 2;

        const clipX = cardX + insetLeft;
        const clipY = cardY + insetTop;
        const clipWidth = cardWidth - insetLeft - insetRight;
        const clipHeight = cardHeight - insetTop - insetBottom;

        exportCtx.beginPath();
        exportCtx.moveTo(clipX + borderRadius, clipY);
        exportCtx.lineTo(clipX + clipWidth - borderRadius, clipY);
        exportCtx.arcTo(
          clipX + clipWidth,
          clipY,
          clipX + clipWidth,
          clipY + borderRadius,
          borderRadius,
        );
        exportCtx.lineTo(clipX + clipWidth, clipY + clipHeight - borderRadius);
        exportCtx.arcTo(
          clipX + clipWidth,
          clipY + clipHeight,
          clipX + clipWidth - borderRadius,
          clipY + clipHeight,
          borderRadius,
        );
        exportCtx.lineTo(clipX + borderRadius, clipY + clipHeight);
        exportCtx.arcTo(
          clipX,
          clipY + clipHeight,
          clipX,
          clipY + clipHeight - borderRadius,
          borderRadius,
        );
        exportCtx.lineTo(clipX, clipY + borderRadius);
        exportCtx.arcTo(
          clipX,
          clipY,
          clipX + borderRadius,
          clipY,
          borderRadius,
        );
        exportCtx.closePath();
        exportCtx.clip();

        exportCtx.drawImage(cardImg, cardX, cardY, cardWidth, cardHeight);

        exportCtx.restore();
      }

      if (token?.symbol) {
        exportCtx.save();
        const fontSize = exportSize * 0.03;

        exportCtx.font = `${fontSize}px Inter, sans-serif`;
        exportCtx.fillStyle = 'white';
        exportCtx.textAlign = 'center';
        exportCtx.textBaseline = 'top';

        const textX = (displayWidth - exportSize) / 2 + exportSize / 3.5;
        const textY = exportSize / 2.1;

        const line1 = "You've been gifted";
        exportCtx.fillText(line1, textX, textY);

        const line2 = token.symbol;
        const lineHeight = fontSize * 1.3;
        exportCtx.fillText(line2, textX, textY + lineHeight);

        exportCtx.restore();
      }

      if (giftCardText) {
        exportCtx.save();

        const fontSize = exportSize * 0.014;
        exportCtx.font = `${fontSize}px Inter, sans-serif`;
        exportCtx.fillStyle = 'white';
        exportCtx.textAlign = 'center';
        exportCtx.textBaseline = 'top';

        const textX = exportX + exportSize / 3.5;
        const textY = exportSize / 1.75;

        const text = giftCardText.slice(0, 190);

        const maxCharsPerLine = 35;

        const words = text;
        const lines = [];
        let currentLine = '';

        for (let index = 0; index < words.length; index++) {
          const word = words[index];
          const followingCurrentLine = currentLine + word;
          if (followingCurrentLine.length > maxCharsPerLine) {
            lines.push(followingCurrentLine);
            currentLine = '';
          } else {
            currentLine = followingCurrentLine;
            if (index === words.length - 1) {
              lines.push(followingCurrentLine);
            }
          }
        }

        const lineHeight = fontSize * 1.2;
        lines.forEach((line, index) => {
          exportCtx.fillText(line, textX, textY + index * lineHeight);
        });

        exportCtx.restore();
      }

      if (senderName) {
        exportCtx.save();

        const fontSize = exportSize * 0.012;
        exportCtx.font = `${fontSize}px Inter, sans-serif`;
        exportCtx.fillStyle = 'white';
        exportCtx.textAlign = 'center';
        exportCtx.textBaseline = 'top';

        const textX = exportX + exportSize / 3.5;
        const textY = exportSize / 1.4;

        const text = `From: ${senderName.slice(0, 32)}`;
        exportCtx.fillText(text, textX, textY);

        exportCtx.restore();
      }

      exportCanvas.toBlob(
        (blob) => {
          setViewerSrc(URL.createObjectURL(blob));
        },
        'image/png',
        1.0,
      );
    };

    const checkAndDraw = () => {
      if (
        backgroundImg.complete &&
        (!cardImg || cardImg.complete || !cardImage)
      ) {
        drawImage();
      }
    };

    backgroundImg.onload = checkAndDraw;
    backgroundImg.src = '/physical/opened.png';

    cardImg.onload = checkAndDraw;
    cardImg.src = cardImage;

    const handleResize = () => {
      drawImage();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [cardImage, token, giftCardText, senderName]);

  return (
    <>
      <PhotoProvider>
        <Canvas ref={canvasRef} style={{ display: 'none' }} />
        <PhotoView src={viewerSrc} defaultVisible={false}>
          <img src={viewerSrc} alt='' style={{ maxWidth: '100%' }} />
        </PhotoView>
      </PhotoProvider>
    </>
  );
};

export default Carrier;
